import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { certificateTemplateService, type CertificateTemplate } from '@/services/certificateTemplateService';
import { axiosClient } from '@/lib/axiosClient';
import { Plus, Edit2, Trash2, ImageIcon, LayoutTemplate, Search, RefreshCw, Bold, Italic, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { usePeriods } from '@/services/usePeriods';
import { useAdminCertificateTypes } from '@/services/adminCertificateTypeService';

export default function CertificateTemplatesPage() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CertificateTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<CertificateTemplate | null>(null);

  // Table Filters & Pagination State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCertificateType, setFilterCertificateType] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Auto-select period
  const { data: periodsData } = usePeriods();
  const activePeriod = periodsData?.find((p: any) => p.is_active);
  const initialPeriodSet = useRef(false);

  useEffect(() => {
    if (activePeriod && !initialPeriodSet.current) {
      setFilterPeriod(String(activePeriod.period_id));
      initialPeriodSet.current = true;
    }
  }, [activePeriod]);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    type: 'convention',
    conference_id: '',
    certificate_type_id: '',
    period_id: '',
    pos_nomor_surat_top: '240',
    pos_sebagai_top: '310',
    pos_nama_peserta_top: '380',
    officials_top_offset: '',
    certificate_number_format: '',
    certificate_date: '',
    certificate_title: '',
    event_name: '',
    event_date_text: '',
    event_venue: '',
    text_top: 'Diberikan kepada',
    text_middle: 'Atas peran dan partisipasinya sebagai',
    text_bottom: '',
    is_active: '1'
  });
  const [file, setFile] = useState<File | null>(null);

  // Queries
  const { data: templates, isLoading } = useQuery({
    queryKey: ['certificate-templates', filterPeriod],
    queryFn: () => certificateTemplateService.getAll(filterPeriod)
  });

  const { data: certificateTypes } = useAdminCertificateTypes();

  const { data: conferences } = useQuery({
    queryKey: ['public-conferences'],
    queryFn: async () => {
      const res = await axiosClient.get('/conference-billing/conferences');
      return Array.isArray(res.data) ? res.data : (res.data?.data || []);
    }
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: certificateTemplateService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificate-templates'] });
      toast.success('Template berhasil ditambahkan');
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Gagal menyimpan template');
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number, data: FormData }) => certificateTemplateService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificate-templates'] });
      toast.success('Template berhasil diperbarui');
      setIsModalOpen(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Gagal memperbarui template');
    }
  });

  const deleteMutation = useMutation({
    mutationFn: certificateTemplateService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['certificate-templates'] });
      toast.success('Template berhasil dihapus');
    },
    onError: () => {
      toast.error('Gagal menghapus template');
    }
  });

  const resetForm = () => {
    setEditingTemplate(null);
    setFile(null);
    setFormData({
      name: '',
      type: 'convention',
      conference_id: '',
      certificate_type_id: '',
      period_id: filterPeriod !== 'all' ? filterPeriod : (activePeriod ? String(activePeriod.period_id) : ''),
      pos_nomor_surat_top: '240',
      pos_sebagai_top: '310',
      pos_nama_peserta_top: '380',
      officials_top_offset: '',
      certificate_number_format: '',
      certificate_date: '',
      certificate_title: '',
      event_name: '',
      event_date_text: '',
      event_venue: '',
      text_top: 'Diberikan kepada',
      text_middle: 'Atas peran dan partisipasinya sebagai',
      text_bottom: '',
      is_active: '1'
    });
  };

  const handleFormat = (tag: string) => {
    const textarea = document.getElementById('text_bottom') as HTMLTextAreaElement;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = formData.text_bottom;
    
    const before = text.substring(0, start);
    const selected = text.substring(start, end);
    const after = text.substring(end);
    
    const newText = `${before}<${tag}>${selected || 'teks'}</${tag}>${after}`;
    
    setFormData({ ...formData, text_bottom: newText });
    
    // Focus back and set selection
    setTimeout(() => {
      textarea.focus();
      if (selected) {
        textarea.setSelectionRange(start, start + selected.length + tag.length * 2 + 5);
      } else {
        textarea.setSelectionRange(start + tag.length + 2, start + tag.length + 2 + 4);
      }
    }, 0);
  };

  const handlePreviewPdf = async (id: number) => {
    const loadingToast = toast.loading('Membuat preview PDF...');
    try {
      const response = await axiosClient.get(`/private/certificate-templates/${id}/preview`, {
        responseType: 'blob',
      });
      const fileURL = URL.createObjectURL(response.data);
      window.open(fileURL, '_blank');
      toast.success('Preview berhasil dibuat', { id: loadingToast });
    } catch (error) {
      console.error(error);
      toast.error('Gagal membuat preview PDF', { id: loadingToast });
    }
  };

  const handleOpenEdit = (t: CertificateTemplate) => {
    setEditingTemplate(t);
    setFormData({
      name: t.name,
      type: t.type,
      conference_id: t.conference_id ? String(t.conference_id) : '',
      certificate_type_id: t.certificate_type_id ? String(t.certificate_type_id) : '',
      period_id: t.period_id ? String(t.period_id) : '',
      pos_nomor_surat_top: String(t.pos_nomor_surat_top),
      pos_sebagai_top: String(t.pos_sebagai_top),
      pos_nama_peserta_top: String(t.pos_nama_peserta_top),
      officials_top_offset: t.officials_top_offset !== null && t.officials_top_offset !== undefined ? String(t.officials_top_offset) : '',
      certificate_number_format: t.certificate_number_format || '',
      certificate_date: t.certificate_date || '',
      certificate_title: t.certificate_title || '',
      event_name: t.event_name || '',
      event_date_text: t.event_date_text || '',
      event_venue: t.event_venue || '',
      text_top: t.text_top || 'Diberikan kepada',
      text_middle: t.text_middle || 'Atas peran dan partisipasinya sebagai',
      text_bottom: t.text_bottom || '',
      is_active: t.is_active ? '1' : '0'
    });
    setFile(null);
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!editingTemplate && !file) {
      toast.error('Background image wajib diunggah untuk template baru');
      return;
    }

    const submitData = new FormData();
    submitData.append('name', formData.name);
    submitData.append('type', formData.type);
    if (formData.type === 'conference' && formData.conference_id) {
      submitData.append('conference_id', formData.conference_id);
    }
    if (formData.certificate_type_id && formData.certificate_type_id !== "none") {
      submitData.append('certificate_type_id', formData.certificate_type_id);
    }
    submitData.append('period_id', formData.period_id);
    submitData.append('pos_nomor_surat_top', formData.pos_nomor_surat_top);
    submitData.append('pos_sebagai_top', formData.pos_sebagai_top);
    submitData.append('pos_nama_peserta_top', formData.pos_nama_peserta_top);
    // Kosong = pakai default lama (320px) - backend menormalkan string kosong jadi NULL.
    submitData.append('officials_top_offset', formData.officials_top_offset);
    submitData.append('certificate_number_format', formData.certificate_number_format);
    submitData.append('certificate_date', formData.certificate_date);
    submitData.append('certificate_title', formData.certificate_title);
    submitData.append('event_name', formData.event_name);
    submitData.append('event_date_text', formData.event_date_text);
    submitData.append('event_venue', formData.event_venue);
    submitData.append('text_top', formData.text_top);
    submitData.append('text_middle', formData.text_middle);
    submitData.append('text_bottom', formData.text_bottom);
    submitData.append('is_active', formData.is_active);

    if (file) {
      submitData.append('background_image', file);
    }

    if (editingTemplate) {
      updateMutation.mutate({ id: editingTemplate.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  // Derived state for filtering and pagination
  const filteredTemplates = templates?.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || t.type === filterType;
    const matchesCertType = filterCertificateType === 'all' || 
                            (filterCertificateType === 'none' && !t.certificate_type_id) || 
                            (t.certificate_type_id && String(t.certificate_type_id) === filterCertificateType);
    return matchesSearch && matchesType && matchesCertType;
  }) || [];

  const totalItems = filteredTemplates.length;
  const totalPages = Math.ceil(totalItems / perPage);
  const startIndex = (page - 1) * perPage;
  const endIndex = Math.min(startIndex + perPage, totalItems);
  const currentTemplates = filteredTemplates.slice(startIndex, endIndex);

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-20 px-4 md:px-0">
      {/* 1. Header Card */}
      <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 md:p-8 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col xl:flex-row xl:items-center justify-between gap-6 relative overflow-hidden">
        <div className="absolute -top-12 -right-12 p-12 opacity-5 pointer-events-none">
          <LayoutTemplate className="w-64 h-64 text-indigo-600" />
        </div>

        <div className="flex items-start gap-5 relative z-10">
          <div className="p-4 rounded-3xl bg-indigo-50 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400 flex-shrink-0 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
            <LayoutTemplate className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-2">
              Manajemen Template Sertifikat
            </h1>
            <p className="text-base text-slate-500 dark:text-slate-400 max-w-2xl">
              Atur dan kelola desain latar belakang (template) untuk sertifikat. Anda dapat mengatur template berdasarkan periode aktif.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden relative">
        {/* Table Header: Filters & Search */}
        <div className="p-5 md:p-6 flex flex-col gap-5 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-200/70 dark:border-slate-800">
          
          {/* Baris 1: Search & Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 w-full">
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Cari nama template..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="w-full pl-9 h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm focus-visible:ring-indigo-500 shadow-sm"
              />
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end flex-shrink-0">
              <Button
                variant="outline"
                onClick={() => queryClient.invalidateQueries({ queryKey: ['certificate-templates'] })}
                disabled={isLoading}
                className="h-11 px-4 rounded-xl border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700 flex-1 sm:flex-none"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
              <Button
                onClick={() => { resetForm(); setIsModalOpen(true); }}
                className="h-11 px-5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm font-bold flex-1 sm:flex-none"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah Template
              </Button>
            </div>
          </div>

          {/* Baris 2: Filter Dropdowns */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
            <Select value={filterPeriod} onValueChange={(val) => { setFilterPeriod(val); setPage(1); }}>
              <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm font-semibold shadow-sm focus:ring-indigo-500">
                <SelectValue placeholder="Pilih Periode Aktif" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                <SelectItem value="all" className="font-medium cursor-pointer rounded-lg m-1">Semua Periode</SelectItem>
                {periodsData?.map((p: any) => (
                  <SelectItem key={p.period_id} value={String(p.period_id)} className="font-medium cursor-pointer rounded-lg m-1">
                    {p.name} {p.is_active ? '(Aktif)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filterType} onValueChange={(val) => { setFilterType(val); setPage(1); }}>
              <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm font-semibold shadow-sm focus:ring-indigo-500">
                <SelectValue placeholder="Semua Tipe" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                <SelectItem value="all" className="font-medium cursor-pointer rounded-lg m-1">Semua Tipe</SelectItem>
                <SelectItem value="convention" className="font-medium cursor-pointer rounded-lg m-1">Konvensi</SelectItem>
                <SelectItem value="conference" className="font-medium cursor-pointer rounded-lg m-1">Konferensi</SelectItem>
                <SelectItem value="mainroom" className="font-medium cursor-pointer rounded-lg m-1">Mainroom</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filterCertificateType} onValueChange={(val) => { setFilterCertificateType(val); setPage(1); }}>
              <SelectTrigger className="w-full !h-11 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 rounded-xl text-sm font-semibold shadow-sm focus:ring-indigo-500">
                <SelectValue placeholder="Semua Tipe Sertifikat" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-slate-200 dark:border-slate-800 shadow-xl">
                <SelectItem value="all" className="font-medium cursor-pointer rounded-lg m-1">Semua Tipe Sertifikat</SelectItem>
                <SelectItem value="none" className="font-medium cursor-pointer rounded-lg m-1">Semua Role / Default</SelectItem>
                {certificateTypes?.map((ct: any) => (
                  <SelectItem key={ct.id} value={String(ct.id)} className="font-medium cursor-pointer rounded-lg m-1">
                    {ct.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Memuat data...</div>
        ) : (
          <div className="overflow-x-auto pb-4">
            <table className="w-full text-left text-sm whitespace-nowrap">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="px-6 py-4">Nama Template</th>
                  <th className="px-6 py-4">Periode</th>
                  <th className="px-6 py-4">Tipe</th>
                  <th className="px-6 py-4">Tipe Sertifikat (Role)</th>
                  <th className="px-6 py-4">Track Konferensi</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-center sticky right-0 bg-slate-50 dark:bg-slate-800/50 shadow-[-4px_0_6px_rgba(0,0,0,0.02)] z-10">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                {currentTemplates.length > 0 ? (
                  currentTemplates.map((t) => (
                    <tr key={t.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/20 transition-colors group">
                      <td className="px-6 py-4 font-medium text-slate-600 dark:text-slate-400">{t.name}</td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{t.period?.name || <span className="text-slate-400 italic">Global</span>}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${t.type === 'convention' ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800/50' : 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800/50'}`}>
                          {t.type.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{t.certificate_type?.name || <span className="text-slate-400 italic">Semua</span>}</td>
                      <td className="px-6 py-4 text-slate-600 dark:text-slate-400">{t.conference?.name || <span className="text-slate-400 italic">Default</span>}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${t.is_active ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800/50' : 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-rose-200 dark:border-rose-800/50'}`}>
                          {t.is_active ? 'AKTIF' : 'TIDAK AKTIF'}
                        </span>
                      </td>
                      <td className="px-6 py-4 flex justify-center space-x-2 sticky right-0 bg-white dark:bg-slate-900 group-hover:bg-slate-50 dark:group-hover:bg-slate-800/50 shadow-[-4px_0_6px_rgba(0,0,0,0.02)] z-10 transition-colors">
                        {t.background_image_url && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => handlePreviewPdf(t.id)} className="text-blue-500 hover:text-blue-600 hover:bg-blue-50" title="Preview PDF">
                              <FileText className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => setPreviewTemplate(t)} className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50" title="Lihat Background">
                              <ImageIcon className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => handleOpenEdit(t)} className="text-indigo-500 hover:text-indigo-600 hover:bg-indigo-50">
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => {
                          if (confirm('Yakin hapus template ini?')) {
                            deleteMutation.mutate(t.id);
                          }
                        }} className="text-rose-500 hover:text-rose-600 hover:bg-rose-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center justify-center text-slate-400">
                        <LayoutTemplate className="w-12 h-12 mb-4 text-slate-300 dark:text-slate-600" />
                        <p className="font-semibold text-base text-slate-600 dark:text-slate-300">Tidak ada template</p>
                        <p className="text-sm mt-1">Coba sesuaikan filter pencarian di atas.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer Pagination */}
        {totalItems > 0 && !isLoading && (
          <div className="p-4 md:p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
            <div className="flex items-center gap-3">
              <span className="text-slate-500 font-medium text-xs sm:text-sm">
                Menampilkan <span className="font-semibold text-slate-700 dark:text-slate-300">{startIndex + 1} - {endIndex}</span> dari <span className="font-semibold text-slate-700 dark:text-slate-300">{totalItems}</span> data
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-xs sm:text-sm font-medium">Per halaman:</span>
                <select
                  value={perPage}
                  onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                  className="bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-lg px-2 py-1.5 outline-none focus:ring-1 focus:ring-indigo-500 font-bold text-slate-700 dark:text-slate-300 text-xs sm:text-sm shadow-sm"
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                  <option value="250">250</option>
                  <option value="500">500</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 sm:h-9 px-3 sm:px-4 rounded-lg font-bold border-slate-200 dark:border-slate-700 text-xs sm:text-sm"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="mr-1 sm:mr-2"><path d="m15 18-6-6 6-6" /></svg>
                  Prev
                </Button>
                <span className="text-xs sm:text-sm font-bold px-2 sm:px-3 text-slate-700 dark:text-slate-300">
                  {page} / {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === (totalPages || 1)}
                  className="h-8 sm:h-9 px-3 sm:px-4 rounded-lg font-bold border-slate-200 dark:border-slate-700 text-xs sm:text-sm"
                >
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="ml-1 sm:ml-2"><path d="m9 18 6-6-6-6" /></svg>
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-3xl max-w-[95vw] max-h-[90vh] overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Edit Template' : 'Tambah Template'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2 min-w-0">
              <Label>Periode</Label>
              <Select value={formData.period_id} onValueChange={v => setFormData({ ...formData, period_id: v })}>
                <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all"><SelectValue placeholder="Pilih Periode" /></SelectTrigger>
                <SelectContent>
                  {periodsData?.map((p: any) => (
                    <SelectItem key={p.period_id} value={String(p.period_id)}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nama Template</Label>
                <Input required value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} placeholder="Contoh: Konvensi Utama" />
              </div>
              <div className="space-y-2 min-w-0">
                <Label>Tipe Sertifikat (Peserta/Presenter)</Label>
                <Select value={formData.certificate_type_id} onValueChange={v => setFormData({ ...formData, certificate_type_id: v })}>
                  <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all"><SelectValue placeholder="Opsional / Pilih Tipe" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Tidak Terikat --</SelectItem>
                    {certificateTypes?.map((c: any) => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2 min-w-0">
              <Label>Tipe (Kategori)</Label>
              <Select value={formData.type} onValueChange={v => setFormData({ ...formData, type: v })}>
                <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="convention">Konvensi (Convention)</SelectItem>
                  <SelectItem value="conference">Konferensi (Conference)</SelectItem>
                  <SelectItem value="mainroom">Mainroom / Plenary Session</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.type === 'conference' && (
              <div className="space-y-2 min-w-0">
                <Label>Track Konferensi (Kosongkan jika ingin jadikan default)</Label>
                <Select value={formData.conference_id} onValueChange={v => {
                  const selectedConf = conferences?.find((c: any) => String(c.conference_id) === v);
                  setFormData({
                    ...formData,
                    conference_id: v,
                    event_name: formData.event_name || (selectedConf ? selectedConf.name : '')
                  });
                }}>
                  <SelectTrigger className="w-full text-left min-w-0 [&>span]:line-clamp-1 [&>span]:break-all"><SelectValue placeholder="Pilih Track / Biarkan Kosong (Default)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value=" ">-- Default Conference Template --</SelectItem>
                    {conferences?.map((c: any) => (
                      <SelectItem key={c.conference_id} value={String(c.conference_id)}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Background Image (PNG/JPG, A4 Landscape)</Label>
              <Input type="file" accept="image/*" onChange={e => setFile(e.target.files?.[0] || null)} />
              {editingTemplate && !file && (
                <div className="flex items-center justify-between mt-3 bg-slate-50 dark:bg-slate-800/50 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Abaikan jika tidak ingin mengubah gambar saat ini.</p>
                  {editingTemplate.background_image_url && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setPreviewTemplate(editingTemplate)}
                      className="h-7 px-3 text-xs flex items-center gap-1.5 border-indigo-200 text-indigo-600 hover:bg-indigo-50 dark:border-indigo-800 dark:text-indigo-400 dark:hover:bg-indigo-900/30 bg-white dark:bg-slate-800 shadow-sm transition-colors"
                    >
                      <ImageIcon className="w-3.5 h-3.5" />
                      Lihat Gambar
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Teks Pengantar Atas (Contoh: Diberikan kepada)</Label>
              <Input value={formData.text_top} onChange={e => setFormData({ ...formData, text_top: e.target.value })} placeholder="Teks di atas nama peserta" />
            </div>

            <div className="space-y-2">
              <Label>Teks Pengantar Tengah (Contoh: Atas peran dan partisipasinya sebagai)</Label>
              <Input value={formData.text_middle} onChange={e => setFormData({ ...formData, text_middle: e.target.value })} placeholder="Teks di bawah nama peserta" />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Teks Deskripsi (Muncul di bawah peran/sebagai)</Label>
                <div className="flex gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => handleFormat('b')} className="h-7 w-7 p-0" title="Tebal (Bold)"><Bold className="w-4 h-4" /></Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => handleFormat('i')} className="h-7 w-7 p-0" title="Miring (Italic)"><Italic className="w-4 h-4" /></Button>
                </div>
              </div>
              <Textarea
                id="text_bottom"
                value={formData.text_bottom}
                onChange={e => setFormData({ ...formData, text_bottom: e.target.value })}
                placeholder="Contoh: Atas partisipasinya sebagai Pemakalah dalam acara Konferensi..."
                rows={4}
              />
              <p className="text-[10px] text-gray-500">Tekan Enter untuk baris baru. Gunakan tombol B/I di atas untuk cetak tebal/miring.</p>
            </div>

            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg border">
              <div className="space-y-2">
                <Label>Posisi Top (px) - No Surat</Label>
                <Input type="number" required value={formData.pos_nomor_surat_top} onChange={e => setFormData({ ...formData, pos_nomor_surat_top: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Posisi Top (px) - Sebagai</Label>
                <Input type="number" required value={formData.pos_sebagai_top} onChange={e => setFormData({ ...formData, pos_sebagai_top: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Posisi Top (px) - Nama</Label>
                <Input type="number" required value={formData.pos_nama_peserta_top} onChange={e => setFormData({ ...formData, pos_nama_peserta_top: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 p-4 bg-gray-50 rounded-lg border">
              <div className="space-y-2">
                <Label>Jarak Blok Tanda Tangan dari No Surat (px) - Opsional</Label>
                <Input
                  type="number"
                  placeholder="Default: 320px (kosongkan jika tidak yakin)"
                  value={formData.officials_top_offset}
                  onChange={e => setFormData({ ...formData, officials_top_offset: e.target.value })}
                />
                <p className="text-[10px] text-gray-500">
                  Mengatur naik/turun blok tanda tangan (Ketua Panitia & Rektor) tanpa mengubah posisi elemen lain.
                  Kosongkan untuk pakai default (320px) - aman untuk template lama.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border">
              <div className="space-y-2">
                <Label>Format Nomor Sertifikat</Label>
                <Input value={formData.certificate_number_format} onChange={e => setFormData({ ...formData, certificate_number_format: e.target.value })} placeholder="Contoh: NOMOR: {NO}/UN48.18/TI.02.03/2026" />
                <p className="text-[10px] text-gray-500">Kosongkan jika tidak perlu dicetak.</p>
              </div>
              <div className="space-y-2">
                <Label>Tanggal Cetak Sertifikat</Label>
                <Input value={formData.certificate_date} onChange={e => setFormData({ ...formData, certificate_date: e.target.value })} placeholder="Contoh: Bali, 5 Oktober 2026" />
                <p className="text-[10px] text-gray-500">Kosongkan jika sudah ada di template gambar.</p>
              </div>
            </div>

            <div className="space-y-2 p-4 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-lg border border-indigo-100 dark:border-indigo-800">
              <Label>Judul Sertifikat (Opsional)</Label>
              <Input value={formData.certificate_title} onChange={e => setFormData({ ...formData, certificate_title: e.target.value })} placeholder="Contoh: SERTIFIKAT" />
              <p className="text-[10px] text-gray-500">Akan dicetak di bagian atas tengah jika template kosongan.</p>
            </div>

            <div className="space-y-2 p-4 bg-slate-50 dark:bg-slate-900/10 rounded-lg border border-slate-200 dark:border-slate-800">
              <div className="space-y-2 mb-4">
                <Label>Event Name [CONFERENCE NAME]</Label>
                <Input value={formData.event_name} onChange={e => setFormData({ ...formData, event_name: e.target.value })} placeholder="KONASPI XII 2026" />
                <p className="text-[10px] text-gray-500">Akan mereplace text [CONFERENCE NAME] di layout teks</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Date [Date]</Label>
                  <Input value={formData.event_date_text} onChange={e => setFormData({ ...formData, event_date_text: e.target.value })} placeholder="3 - 5 Oktober 2026" />
                  <p className="text-[10px] text-gray-500">Akan mereplace text [Date] di layout teks</p>
                </div>
                <div className="space-y-2">
                  <Label>Event Venue [Venue/Location]</Label>
                  <Input value={formData.event_venue} onChange={e => setFormData({ ...formData, event_venue: e.target.value })} placeholder="Universitas ..." />
                  <p className="text-[10px] text-gray-500">Akan mereplace text [Venue/Location] atau [VENUE] di layout teks</p>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={formData.is_active} onValueChange={v => setFormData({ ...formData, is_active: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Aktif</SelectItem>
                  <SelectItem value="0">Tidak Aktif</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Batal</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {createMutation.isPending || updateMutation.isPending ? 'Menyimpan...' : 'Simpan Template'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!previewTemplate} onOpenChange={(open) => !open && setPreviewTemplate(null)}>
        <DialogContent className="sm:max-w-4xl max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>Pratinjau Template Sertifikat</DialogTitle>
          </DialogHeader>
          {previewTemplate && (
            <div className="space-y-4">
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                <h3 className="font-semibold text-slate-800 dark:text-slate-200">{previewTemplate.name}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Template ini digunakan untuk cetak:
                  <span className="font-bold ml-1 text-indigo-600 dark:text-indigo-400">
                    Sertifikat {previewTemplate.type === 'convention' ? 'Konvensi' : previewTemplate.type === 'mainroom' ? 'Mainroom' : 'Konferensi'}
                    {previewTemplate.type === 'conference' && previewTemplate.conference ? ` - Track ${previewTemplate.conference.name}` : ''}
                    {previewTemplate.type === 'conference' && !previewTemplate.conference ? ' (Default / Semua Track)' : ''}
                    {previewTemplate.certificate_type ? ' - Khusus Tipe: ' + previewTemplate.certificate_type.name : ''}
                  </span>
                </p>
              </div>
              <div className="flex justify-center bg-gray-100 dark:bg-slate-950 p-4 rounded-lg border dark:border-slate-800 overflow-hidden">
                <img
                  src={previewTemplate.background_image_url || undefined}
                  alt={previewTemplate.name}
                  className="max-h-[60vh] object-contain shadow-sm border border-gray-200 dark:border-slate-700 rounded"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewTemplate(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
